import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import Sparkline from "@/components/Sparkline";
import {
    TrendUp, TrendDown, Users, ChartLine, Funnel,
    WarningCircle, ArrowsCounterClockwise, CurrencyInr, Sparkle,
    Star, ThumbsUp, PaperPlaneTilt,
} from "@phosphor-icons/react";

const fmtInt = (n) => Number(n || 0).toLocaleString("en-IN");
const fmtInr = (n) => "₹" + Number(n || 0).toLocaleString("en-IN", { maximumFractionDigits: 0 });

const SOURCE_STYLE = {
    referral: { color: "#00D4E4", label: "Referral" },
    seo:      { color: "#22c55e", label: "Organic SEO" },
    paid:     { color: "#f59e0b", label: "Paid ads" },
    direct:   { color: "#525252", label: "Direct" },
    other:    { color: "#a3a3a3", label: "Other" },
};

export default function SuperadminGrowthDashboard() {
    const [data, setData] = useState(null);
    const [error, setError] = useState(null);
    const [loading, setLoading] = useState(true);
    const [refreshing, setRefreshing] = useState(false);

    const load = async (silent = false) => {
        if (!silent) setLoading(true); else setRefreshing(true);
        try {
            const r = await api.get("/superadmin/growth-metrics");
            setData(r.data);
            setError(null);
        } catch (err) {
            setError(err?.response?.data?.detail || "Couldn't fetch metrics");
        } finally {
            setLoading(false);
            setRefreshing(false);
        }
    };

    useEffect(() => { load(); }, []);

    if (loading) {
        return (
            <div className="p-10 text-sm text-[#525252]" data-testid="growth-loading">
                <Sparkle size={20} weight="fill" className="text-splash animate-pulse inline mr-2" />
                Aggregating growth metrics…
            </div>
        );
    }

    if (error) {
        return (
            <div className="p-10" data-testid="growth-error">
                <WarningCircle size={24} weight="fill" className="text-amber-500 mb-2" />
                <p className="text-sm text-red-500">{error}</p>
            </div>
        );
    }

    const { signups, by_source_30d, funnel, top_dropoffs, revenue, references_90d } = data;
    const wowUp = signups.wow_delta_pct >= 0;
    const sourceTotal = Object.values(by_source_30d).reduce((a, b) => a + b, 0) || 1;
    const maxFunnel = funnel[0]?.count || 1;

    return (
        <div className="p-4 sm:p-8 space-y-6" data-testid="growth-dashboard">
            {/* Header */}
            <div className="flex items-baseline justify-between flex-wrap gap-3">
                <div>
                    <p className="text-[10px] font-black uppercase tracking-[0.25em] text-splash mb-1 inline-flex items-center gap-1">
                        <ChartLine size={11} weight="fill" /> V1.0 · Growth
                    </p>
                    <h1 className="font-heading text-3xl sm:text-4xl font-black tracking-tighter text-ink">
                        Signups, funnel, and where users drop off.
                    </h1>
                    <p className="text-xs text-[#A3A3A3] mt-1">
                        Refreshed {new Date(data.generated_at).toLocaleString("en-IN")}
                    </p>
                </div>
                <button onClick={() => load(true)} disabled={refreshing}
                        data-testid="growth-refresh-btn"
                        className="text-[10px] font-black uppercase tracking-wider px-3 py-2 border-2 border-ink bg-white hover:bg-ink hover:text-white transition-colors inline-flex items-center gap-1 disabled:opacity-50">
                    <ArrowsCounterClockwise size={11} weight="bold" className={refreshing ? "animate-spin" : ""} />
                    {refreshing ? "Refreshing…" : "Refresh"}
                </button>
            </div>

            {/* Signup KPI cards */}
            <div className="grid gap-3 sm:grid-cols-4">
                <KpiCard testid="growth-kpi-7d" icon={Users} label="Signups · last 7d"
                         value={fmtInt(signups.last_7d)}
                         sub={<span className={`inline-flex items-center gap-1 ${wowUp ? "text-emerald-600" : "text-red-500"}`}>
                             {wowUp ? <TrendUp size={11} weight="bold" /> : <TrendDown size={11} weight="bold" />}
                             {Math.abs(signups.wow_delta_pct)}% WoW
                         </span>} />
                <KpiCard testid="growth-kpi-30d" icon={Users} label="Signups · last 30d" value={fmtInt(signups.last_30d)} />
                <KpiCard testid="growth-kpi-total" icon={Users} label="Total candidates" value={fmtInt(signups.total_candidates)} />
                <KpiCard testid="growth-kpi-rev7d" icon={CurrencyInr}  label="Revenue · last 7d" value={fmtInr(revenue.last_7d_inr)}
                         sub={<span className="text-[#A3A3A3]">30d · {fmtInr(revenue.last_30d_inr)}</span>} />
            </div>

            {/* By source */}
            <div className="bg-white border border-[#E5E5E5] p-5 sm:p-6" data-testid="growth-by-source">
                <div className="flex items-baseline justify-between mb-4">
                    <div>
                        <p className="text-[10px] font-black uppercase tracking-[0.2em] text-[#525252]">Signup source · last 30d</p>
                        <p className="font-heading text-lg font-black text-ink">Where did they come from?</p>
                    </div>
                    <p className="text-xs text-[#A3A3A3]">{fmtInt(sourceTotal)} total</p>
                </div>
                <div className="space-y-2">
                    {Object.entries(by_source_30d).map(([src, count]) => {
                        const style = SOURCE_STYLE[src] || SOURCE_STYLE.other;
                        const pct = Math.round((count / sourceTotal) * 100);
                        return (
                            <div key={src} data-testid={`growth-source-${src}`} className="grid grid-cols-[100px_1fr_60px] items-center gap-3">
                                <p className="text-xs font-black text-ink">{style.label}</p>
                                <div className="h-3 bg-[#FAFAFA] relative">
                                    <div className="h-full" style={{ width: `${pct}%`, background: style.color }} />
                                </div>
                                <p className="text-xs text-[#525252] text-right">
                                    <b className="text-ink">{fmtInt(count)}</b> · {pct}%
                                </p>
                            </div>
                        );
                    })}
                </div>
            </div>

            {/* Funnel */}
            <div className="bg-white border border-[#E5E5E5] p-5 sm:p-6" data-testid="growth-funnel">
                <div className="mb-4">
                    <p className="text-[10px] font-black uppercase tracking-[0.2em] text-[#525252] inline-flex items-center gap-1">
                        <Funnel size={10} weight="fill" /> Conversion funnel
                    </p>
                    <p className="font-heading text-lg font-black text-ink">Signup → Placement</p>
                </div>
                <div className="space-y-2">
                    {funnel.map((f, i) => {
                        const width = Math.max(4, (f.count / maxFunnel) * 100);
                        return (
                            <div key={i} data-testid={`growth-funnel-${f.stage.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                                 className="grid grid-cols-[140px_1fr_100px] items-center gap-3">
                                <p className="text-xs font-black text-ink">{f.stage}</p>
                                <div className="h-8 bg-[#FAFAFA] relative">
                                    <div className="h-full bg-cobalt/80 flex items-center px-2"
                                         style={{ width: `${width}%` }}>
                                        <p className="text-[10px] font-black text-white truncate">{fmtInt(f.count)}</p>
                                    </div>
                                </div>
                                <p className="text-xs text-[#525252] text-right">{f.pct_from_signup}%</p>
                            </div>
                        );
                    })}
                </div>
            </div>

            {/* Reference-check analytics (V1.3, Feb 2026) — reference module hiring signals */}
            {references_90d && references_90d.total_requested > 0 && (
                <div className="bg-white border border-[#E5E5E5] p-5 sm:p-6" data-testid="growth-references">
                    <div className="flex items-baseline justify-between mb-4">
                        <div>
                            <p className="text-[10px] font-black uppercase tracking-[0.2em] text-emerald-600 inline-flex items-center gap-1">
                                <ThumbsUp size={11} weight="fill" /> Reference checks · last 90d
                            </p>
                            <p className="font-heading text-lg font-black text-ink">
                                {fmtInt(references_90d.responses)} of {fmtInt(references_90d.surveys_sent)} referees responded
                                <span className="ml-2 text-sm text-[#525252]">({references_90d.response_rate_pct}%)</span>
                            </p>
                        </div>
                        {references_90d.trend_30d && references_90d.trend_30d.length > 0 && (
                            <div className="hidden sm:block text-right" data-testid="growth-references-trend">
                                <p className="text-[10px] font-black uppercase tracking-[0.2em] text-[#A3A3A3] mb-1">
                                    Response rate · last 30d
                                </p>
                                <Sparkline
                                    testid="growth-references-sparkline"
                                    points={references_90d.trend_30d.map((d) => ({ date: d.date, value: d.response_rate }))}
                                    width={220}
                                    height={44}
                                />
                            </div>
                        )}
                    </div>
                    <div className="grid gap-3 sm:grid-cols-4">
                        <KpiCard
                            testid="ref-kpi-total"
                            icon={PaperPlaneTilt}
                            label="Requested (all)"
                            value={fmtInt(references_90d.total_requested)}
                            sub={<span className="text-[#A3A3A3]">Sent: {fmtInt(references_90d.surveys_sent)}</span>}
                        />
                        <KpiCard
                            testid="ref-kpi-response-rate"
                            icon={ChartLine}
                            label="Response rate"
                            value={`${references_90d.response_rate_pct}%`}
                            sub={<span className="text-[#A3A3A3]">{fmtInt(references_90d.responses)} submitted</span>}
                        />
                        <KpiCard
                            testid="ref-kpi-rating"
                            icon={Star}
                            label="Avg rating"
                            value={references_90d.avg_rating != null ? `${references_90d.avg_rating}/5` : "—"}
                            sub={
                                references_90d.avg_rating != null ? (
                                    <span className="inline-flex items-center gap-0.5 text-amber-500">
                                        {[1, 2, 3, 4, 5].map((n) => (
                                            <Star key={n} size={10} weight={references_90d.avg_rating >= n ? "fill" : "regular"} />
                                        ))}
                                    </span>
                                ) : <span className="text-[#A3A3A3]">No responses yet</span>
                            }
                        />
                        <KpiCard
                            testid="ref-kpi-rehire"
                            icon={ThumbsUp}
                            label="Would rehire"
                            value={references_90d.pct_would_rehire != null ? `${references_90d.pct_would_rehire}%` : "—"}
                            sub={
                                <span className="text-[#A3A3A3]">
                                    Recommend for role: <b className="text-ink">
                                        {references_90d.pct_recommend != null ? `${references_90d.pct_recommend}%` : "—"}
                                    </b>
                                </span>
                            }
                        />
                    </div>
                </div>
            )}

            {/* Drop-off alerts */}
            {top_dropoffs.length > 0 && (
                <div className="bg-white border border-[#E5E5E5] p-5 sm:p-6" data-testid="growth-dropoffs">
                    <p className="text-[10px] font-black uppercase tracking-[0.2em] text-red-500 inline-flex items-center gap-1 mb-1">
                        <WarningCircle size={10} weight="fill" /> Biggest drop-offs
                    </p>
                    <p className="font-heading text-lg font-black text-ink mb-4">Fix these first in V1.1</p>
                    <div className="space-y-2">
                        {top_dropoffs.map((d, i) => (
                            <div key={i} data-testid={`growth-dropoff-${i}`}
                                 className="flex items-center justify-between p-3 border border-red-200 bg-red-50">
                                <div className="text-sm">
                                    <b className="text-ink">{d.from}</b>
                                    <span className="mx-2 text-[#A3A3A3]">→</span>
                                    <b className="text-ink">{d.to}</b>
                                </div>
                                <div className="text-right">
                                    <p className="text-lg font-heading font-black text-red-500">{fmtInt(d.dropped)}</p>
                                    <p className="text-[10px] text-[#525252]">{d.drop_pct}% dropped</p>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
}

function KpiCard({ testid, icon: Icon, label, value, sub }) {
    return (
        <div data-testid={testid} className="bg-white border border-[#E5E5E5] p-4">
            <div className="flex items-center justify-between mb-2">
                <p className="text-[10px] font-black uppercase tracking-wider text-[#A3A3A3]">{label}</p>
                <Icon size={14} weight="bold" className="text-[#A3A3A3]" />
            </div>
            <p className="font-heading text-3xl font-black tracking-tighter text-ink">{value}</p>
            {sub && <p className="text-[10px] mt-1">{sub}</p>}
        </div>
    );
}
